<template>
  <div class="overstory-container">
    <template v-if="hasOverstoryContent">
      <div class="flow-dates-row p-d-flex">
        <div v-for="(month) in currentFlowDisplayMonths" :key="month.index" class="cell"
          :style="{ width: (month.numDays * dayWidth).toString() + '%' }">
          <div class="dates p-d-flex">
            <div v-for="week in month.weeks" :key="week.index" class="date"
              :class="{ 'starts-mid-week': week.startsMidWeek }" :style="{
                width:
                  Number((week.numDays / month.numDays) * 100).toString() + '%',
              }">
              {{ !month.monthStartsOnWeekStartDay && week.startsMidWeek ? '' : week.firstDay }}
            </div>
          </div>
        </div>
      </div>
      <Accordion :multiple="true" :activeIndex="accordionActiveIndicies" class="widgets-accordion flow-accordion"
        @tab-open="onAccordionTabOpen" @tab-close="onAccordionTabClose">
        <AccordionTab header="Calendars of Interest" :disabled="!filteredSubscriptionEvents.length">
          <template #default v-if="filteredSubscriptionEvents.length">
            <div class="flow-events-row p-d-flex">
              <div class="events-container">
                <CalendarFlowRow :planningItems="filteredSubscriptionEvents" :dayWidth="dayWidth"
                  :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="true" :shouldUseChannelUiColor="true"
                  :isSubscription="true" :instanceIndex="0" />
              </div>
            </div>
          </template>
        </AccordionTab>
        <AccordionTab :disabled="!filteredInitiatives.length">
          <template #header>
            <div class="p-d-flex p-jc-between p-ai-center" style="width: 100%;">
              <div class="p-accordion-header-text">Initiatives</div>
            </div>
          </template>
          <template #default v-if="filteredInitiatives.length">
            <div class="flow-events-row p-d-flex">
              <div class="events-container">
                <CalendarFlowRow :planningItems="filteredInitiatives" :dayWidth="dayWidth"
                  :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="true" :shouldUseChannelUiColor="true"
                  :instanceIndex="1"
                  @showTactic="showInitiative" @showContextMenu="showContextMenu"
                  @showPlanningItemSummaryOverlay="showTacticSummaryOverlay"
                  @hidePlanningItemSummaryOverlay="hideTacticSummaryOverlay" />
              </div>
            </div>
          </template>
        </AccordionTab>
        <AccordionTab header="Monthly Channel Focus" :disabled="!monthlyFocusTactics.length">
          <template #default v-if="monthlyFocusTactics.length">
            <div class="flow-events-row p-d-flex">
              <div class="events-container">
                <CalendarFlowRow :planningItems="monthlyFocusTactics" :dayWidth="dayWidth"
                  :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="true" :shouldUseChannelUiColor="true"
                  :instanceIndex="2"
                  @showTactic="showTactic" @showContextMenu="showContextMenu"
                  @showPlanningItemSummaryOverlay="showTacticSummaryOverlay"
                  @hidePlanningItemSummaryOverlay="hideTacticSummaryOverlay" />
              </div>
            </div>
          </template>
        </AccordionTab>
        <AccordionTab header="Long-Running" :disabled="!filteredPaidChannelTactics.length">
          <template #default v-if="filteredPaidChannelTactics.length">
            <div class="flow-events-row p-d-flex">
              <div class="events-container">
                <CalendarFlowRow :planningItems="filteredPaidChannelTactics" :dayWidth="dayWidth"
                  :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="true" :shouldUseChannelUiColor="true"
                  :instanceIndex="3"
                  @showTactic="showTactic" @showContextMenu="showContextMenu"
                  @showPlanningItemSummaryOverlay="showTacticSummaryOverlay"
                  @hidePlanningItemSummaryOverlay="hideTacticSummaryOverlay" />
              </div>
            </div>
          </template>
        </AccordionTab>
      </Accordion>
    </template>

    <ContextMenu ref="menu" :model="contextMenuItems" appendTo="body" />

    <OverlayPanel ref="planningItemSummaryOverlay" class="planning-item-summary-overlay" appendTo="body">
      <PlanningItemSummary :planningItemId="currentSummaryPlanningItemId" :instanceStartDate="currentSummaryInstanceStartDate" :instanceEndDate="currentSummaryInstanceEndDate" />
    </OverlayPanel>

    <Dialog header="Distribute Tactic to Other Plans" :visible.sync="shouldShowDistributeTacticModal" :modal="true">
      <DistributeTacticModal :tacticId="rightClickedTacticId" @hide="shouldShowDistributeTacticModal = false" />
    </Dialog>

    <Dialog header="Create Nested Variants" :visible.sync="shouldShowDistributeInitiativeModal" :modal="true">
      <DistributeInitiativeModal :initiativeId="rightClickedTacticId"
        @hide="shouldShowDistributeInitiativeModal = false" />
    </Dialog>

  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Channel from '@/models/Channel'
import Tactic from '@/models/Tactic'
import ID from '@/models/ID'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Subscription from '@/models/Subscription'
import OverlayPanel from 'primevue/overlaypanel'
import PlanningItemSummary from '@/views/PlanningItemSummary.vue'
import CalendarFlowRow from '@/components/CalendarFlowRow.vue'
import FlowDisplayMonth from '@/models/FlowDisplayMonth'
import ContextMenuItem from '@/models/ContextMenuItem'
import ContextMenu from 'primevue/contextmenu'
import RelatedInitiativeMapping from '@/models/RelatedInitiativeMapping'
import Initiative from '@/models/Initiative'
import PlanningItem from '@/models/InterfacePlanningItem'
import DistributeTacticModal from '@/components/DistributeTacticModal.vue'
import DistributeInitiativeModal from './DistributeInitiativeModal.vue'
import TacticDisplayMixin from '@/mixins/TacticDisplayMixin'

Vue.component('OverlayPanel', OverlayPanel)
Vue.component('Accordion', Accordion)
Vue.component('AccordionTab', AccordionTab)
Vue.component('ContextMenu', ContextMenu)


export default Vue.extend({
  name: 'CalendarOverstory',
  mixins: [TacticDisplayMixin],
  components: {
    CalendarFlowRow,
    PlanningItemSummary,
    DistributeTacticModal,
    DistributeInitiativeModal,
  },
  data: () => {
    return {
      window: window,
      currentSummaryPlanningItemId: {} as ID,
      currentSummaryInstanceStartDate: null as Date | null,
      currentSummaryInstanceEndDate: null as Date | null,
      rightClickedTacticId: {} as ID,
      rightClickedTacticIsInitiative: false as boolean,
      rightClickedTacticIsInitiativeCustomization: false as boolean,
      rightClickedTacticIsLead: false as boolean,
      rightClickedTacticIsNested: false as boolean,
      rightClickedTacticHasCustomizationForCurrentPlan: false as boolean,
      rightClickedTacticHasCustomizationsForAllNestedPlans: false as boolean,
      rightClickedTacticChannelIsNested: false as boolean,
      millisecondsPerDay: (1000 * 60 * 60 * 24) as number,
      dayWidthTargetPx: 30 as number,
      currentSummaryTimeout: 0,
      isDisplayingSummaryPlanningItem: false as boolean,
      updateItterator: 0 as number,
      shouldShowDistributeTacticModal: false as boolean,
      shouldShowDistributeInitiativeModal: false as boolean,
    }
  },
  computed: {
    dateRange(): Date[] {
      return this.$store.getters.currentDateRange
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    isNestedPlan(): boolean {
      return this.$store.getters.currentPlan.parentId.intID !== 0 ? true : false
    },
    dayWidth(): number {
      if (!this.dateRange[1] || this.dateRange[0] == this.dateRange[1]) {
        return 100
      }
      const utc1 = Date.UTC(
        this.dateRange[0].getFullYear(),
        this.dateRange[0].getMonth(),
        this.dateRange[0].getDate()
      )
      const utc2 = Date.UTC(
        this.dateRange[1].getFullYear(),
        this.dateRange[1].getMonth(),
        this.dateRange[1].getDate()
      )
      // Calculate milliseconds between start and end dates and add one day so last day of range is displayed
      return 100 / ((utc2 - utc1 + this.millisecondsPerDay) / this.millisecondsPerDay)
    },
    monthlyFocusTactics(): Tactic[] {
      const returnArray = [] as Tactic[]
      this.$store.getters.selectedChannels.forEach((channel) => {
        if (channel.useMonthlyFocusType == true) {
          channel.tacticTypes.forEach((type) => {
            if (
              this.$store.getters.selectedTacticTypes.map((type) => type.id.intID).includes(type.id.intID) &&
              type.name.toLowerCase() == this.$store.getters.monthlyFocusTypeName
            ) {
              const tacticsWithClonesForType = type.getTacticsWithClonesForDateRange(this.dateRange) as Tactic[]
              tacticsWithClonesForType.forEach((tactic) => {
                if (
                  !(this as any).isTacticInFilteredOutPlan(tactic) &&
                  !(this as any).isTacticRelatedToDeselectedTag(tactic) &&
                  !(this as any).isTacticRelatedToDeselectedPlatform(tactic) &&
                  !(this as any).isTacticRelatedToDeselectedInitiative(tactic) &&
                  (this as any).isTacticExactMonth(tactic)
                ) {
                  returnArray.push(tactic)
                }
              })
            }
          })
        }
      })
      return returnArray
    },
    filteredSubscriptionEvents(): Tactic[] {
      const returnArray = [] as Tactic[]
      this.$store.getters.selectedSubscriptions.forEach((subscription) => {
        const subscriptionCopy = { ...subscription }

        // Add recurrence clones
        this.$store.dispatch('addRecurringEventClones', subscriptionCopy)

        subscriptionCopy.events.forEach((tactic) => {
          if (
            (this as any).isTacticTouchingDateRange(tactic) &&
            (this as any).isTacticLongerThanThreshold(tactic, 21) &&
            !this.isEventDuplicate(tactic, subscriptionCopy)
          ) {
            returnArray.push(tactic)
          }
        })
      })
      return returnArray
    },
    filteredInitiatives(): PlanningItem[] {
      const useAll = (this as any).filterNoInitiative || (this as any).filterNoTag
      const allItems = useAll
        ? [...this.$store.getters.currentInitiatives, ...this.$store.getters.currentInitiativeCustomizations]
        : [...this.$store.getters.selectedInitiatives, ...this.$store.getters.selectedInitiativeCustomizations]
      const selectedInitiativesAndCustomizations = allItems
      const returnArray = [] as PlanningItem[]
      selectedInitiativesAndCustomizations.forEach((planningItem) => {
        if (
          !(this as any).isTacticInFilteredOutPlan(planningItem) &&
          (this as any).isTacticTouchingDateRange(planningItem)
        ) {
          returnArray.push(planningItem)
        }
      })
      returnArray.sort((a: PlanningItem, b: PlanningItem) => {
        if (a.isLead && !b.isLead) {
          return -1
        }
        if (b.isLead && !a.isLead) {
          return 1
        }
        return 0
      })
      return returnArray
    },
    filteredPaidChannelTactics(): Tactic[] {
      const returnArray = [] as Tactic[]
      this.$store.getters.selectedTacticTypes.forEach((type) => {
        const tacticsWithClonesForType = type.getTacticsWithClonesForDateRange(this.dateRange) as Tactic[]
        tacticsWithClonesForType.forEach((tactic) => {
          if (
            !(this as any).isTacticInFilteredOutPlan(tactic) &&
            !(this as any).isTacticRelatedToDeselectedTag(tactic) &&
            !(this as any).isTacticRelatedToDeselectedPlatform(tactic) &&
            !(this as any).isTacticRelatedToDeselectedInitiative(tactic) &&
            (this as any).isTacticLongerThanThreshold(tactic, 21) &&
            (this as any).isTacticInPaidChannel(tactic) &&
            (this as any).isTacticTouchingDateRange(tactic)
          ) {
            returnArray.push(tactic)
          }
        })
      })
      return returnArray
    },
    // Hide the whole overstory, including the dates row, when every section is empty
    hasOverstoryContent(): boolean {
      return (
        this.filteredSubscriptionEvents.length > 0 ||
        this.filteredInitiatives.length > 0 ||
        this.monthlyFocusTactics.length > 0 ||
        this.filteredPaidChannelTactics.length > 0
      )
    },
    accordionActiveIndicies(): number[] {
      const returnArray = [] as number[]
      this.$store.getters.currentDateRange //Hack to force computed property update when date range changes
      if (
        window.localStorage.getItem('mh-subscriptions') != 'false' &&
        this.filteredSubscriptionEvents.length
      ) {
        returnArray.push(0)
      }
      if (
        window.localStorage.getItem('mh-initiatives') != 'false' &&
        this.filteredInitiatives.length
      ) {
        returnArray.push(1)
      }
      if (
        window.localStorage.getItem('mh-monthlyfocus') != 'false' &&
        this.monthlyFocusTactics.length
      ) {
        returnArray.push(2)
      }
      if (
        window.localStorage.getItem('mh-inmarketmedia') != 'false' &&
        this.filteredPaidChannelTactics.length
      ) {
        returnArray.push(3)
      }
      return returnArray
    },
    currentFlowDisplayMonths(): FlowDisplayMonth[] {
      const returnArray = [] as FlowDisplayMonth[]
      if (!this.dateRange[0] || !this.dateRange[1]) return returnArray

      const tempDate = new Date(this.dateRange[0].toString())
      let numMonths =
        (this.dateRange[1].getFullYear() - this.dateRange[0].getFullYear()) * 12
      numMonths -= this.dateRange[0].getMonth()
      numMonths += this.dateRange[1].getMonth()

      for (let i = 0; i <= numMonths; i += 1) {
        if (i > 0) {
          tempDate.setMonth(tempDate.getMonth() + 1)
        }
        returnArray.push(
          new FlowDisplayMonth(
            i,
            tempDate.toLocaleString('default', {
              month: 'short',
            }),
            i == 0 ? this.dateRange[0].getDate() : 1,
            i == numMonths
              ? this.dateRange[1].getDate()
              : new Date(
                tempDate.getFullYear(),
                tempDate.getMonth() + 1,
                0
              ).getDate(),
            tempDate.getMonth(),
            tempDate.getFullYear(),
            this.$store.getters.currentPlan.weekStartDay
          )
        )
      }
      return returnArray
    },
    contextMenuItems(): ContextMenuItem[] {
      const returnArray = [] as ContextMenuItem[]
      if (this.shouldShowCopyContextMenuItem) {
        returnArray.push({
          label: 'Copy',
          icon: 'pi pi-fw pi-copy',
          command: this.openTacticDetailWithCopyId,
        })
      }
      if (this.shouldShowDistributeContextMenuItem) {
        returnArray.push({
          label: 'Distribute',
          icon: 'pi pi-fw pi-arrow-right',
          command: this.copyTacticToOtherPlans,
        })
      }
      if (this.shouldShowInitiativeCustomizationContextMenuItem) {
        returnArray.push({
          label: 'Create Nested Variant',
          icon: 'pi pi-fw pi-link',
          command: this.openInitiativeCustomizationDetailWithCopyId,
        })
      }
      if (this.shouldShowDistributeInitiativeContextMenuItem) {
        returnArray.push({
          label: 'Create Nested Variants',
          icon: 'pi pi-fw pi-link',
          command: this.createInitiativeVariantsInNestedPlans,
        })
      }
      return returnArray
    },
    shouldShowCopyContextMenuItem(): boolean {
      if (
        (this.isLeadPlan && !this.rightClickedTacticIsLead) ||
        (this.isNestedPlan && this.rightClickedTacticIsLead) ||
        this.rightClickedTacticIsInitiativeCustomization
      ) {
        return false
      }
      return true
    },
    shouldShowDistributeContextMenuItem(): boolean {
      return !this.rightClickedTacticIsInitiative && !this.rightClickedTacticIsInitiativeCustomization && !this.rightClickedTacticChannelIsNested && (this.isLeadPlan || this.isNestedPlan)
    },
    shouldShowInitiativeCustomizationContextMenuItem(): boolean {
      return this.rightClickedTacticIsInitiative && this.rightClickedTacticIsLead && this.isNestedPlan && !this.rightClickedTacticHasCustomizationForCurrentPlan
    },
    shouldShowDistributeInitiativeContextMenuItem(): boolean {
      return this.rightClickedTacticIsInitiative && this.rightClickedTacticIsLead && this.isLeadPlan && !this.rightClickedTacticHasCustomizationsForAllNestedPlans
    },
  },
  beforeRouteLeave(to, from, next) {
    window.clearTimeout(this.currentSummaryTimeout)
    next()
  },
  beforeDestroy() {
    window.clearTimeout(this.currentSummaryTimeout)
  },
  methods: {
    // TODO: add to logic for long-running
    isEventDuplicate(event: Tactic, subscription: Subscription): boolean {
      let returnValue = false
      const duplicateMappingsForTactic = subscription.duplicateEventsMap.filter(
        (duplicateMapping) => duplicateMapping.eventId == event.id.intID
      )
      // Only check for duplicates if there is a duplicate for the provided event
      if (duplicateMappingsForTactic.length) {
        duplicateMappingsForTactic.forEach((duplicateMapping) => {
          // Check if subscription with duplicate is visible
          const selectedSubscriptionWithDuplicate =
            this.$store.getters.selectedSubscriptions.find(
              (selectedSubscription) =>
                selectedSubscription.id.intID ==
                duplicateMapping.duplicateSubscriptionId
            )
          if (selectedSubscriptionWithDuplicate) {
            // Check if subscription with duplicate has a lower ID value than the provided subscription - event with lowest subscription ID will be shown
            if (
              duplicateMapping.duplicateSubscriptionId <
              duplicateMapping.subscriptionId
            ) {
              returnValue = true
            }
          }
        })
      }
      return returnValue
    },
    getAccordionTabNameForIndex(index: number): string {
      switch (index) {
        case 0:
          return 'subscriptions'
        case 1:
          return Channel.CHANNEL_NAME_INITIATIVES.toLowerCase()
        case 2:
          return 'monthlyfocus'
        case 3:
          return 'inmarketmedia'
        default:
          return ''
      }
    },
    onAccordionTabOpen(event) {
      window.localStorage.setItem(
        'mh-' + this.getAccordionTabNameForIndex(event.index),
        'true'
      )
    },
    onAccordionTabClose(event) {
      window.localStorage.setItem(
        'mh-' + this.getAccordionTabNameForIndex(event.index),
        'false'
      )
    },
    showTactic(tactic: Tactic) {
      const thisTacticIntID = tactic.originalTacticID ? tactic.originalTacticID.intID : tactic.id.intID
      this.$router.push({
        path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/tactic/${thisTacticIntID}`,
      })
    },
    showInitiative(planningItem: PlanningItem) {
      if (planningItem.isInitiativeCustomization) {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${planningItem.initiativeId?.intID}/nested-variant/${planningItem.id.intID}`,
        })
      } else {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${planningItem.id.intID}`,
        })
      }
    },
    showContextMenu({ e, planningItem }) {
      this.rightClickedTacticId = planningItem.id
      this.rightClickedTacticIsInitiative = planningItem.isInitiative
      this.rightClickedTacticIsInitiativeCustomization = planningItem.isInitiativeCustomization
      this.rightClickedTacticIsLead = planningItem.isLead
      this.rightClickedTacticIsNested = planningItem.isNested
      this.rightClickedTacticHasCustomizationForCurrentPlan = planningItem.initiativeCustomizations?.filter((customization) => customization.planId.intID === this.$store.getters.currentPlan.id.intID).length > 0
      this.rightClickedTacticHasCustomizationsForAllNestedPlans = this.$store.getters.currentPlan.plans?.every((nestedPlanId) => planningItem.initiativeCustomizations?.some((customization) => customization.planId.intID === nestedPlanId.intID))
      const channelForRightClickedTactic: Channel | undefined = this.$store.getters.currentChannels.find((ch: Channel) => ch.id.intID === planningItem.tacticType?.channelId.intID)
      this.rightClickedTacticChannelIsNested = channelForRightClickedTactic?.isNested ?? false

      if (this.contextMenuItems.length) {
        (this.$refs.menu as Vue & { show: (e) => boolean }).show(e)
      }
    },
    openTacticDetailWithCopyId() {
      this.$router.push({
        path:
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/${this.rightClickedTacticIsInitiative ? 'initiative' : 'tactic'}/0?copyof=${this.rightClickedTacticId.intID}`
      })
    },
    openInitiativeCustomizationDetailWithCopyId() {
      this.$router.push({
        path:
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${this.rightClickedTacticId.intID}/nested-variant/0`
      })
    },
    copyTacticToOtherPlans() {
      this.shouldShowDistributeTacticModal = true
    },
    createInitiativeVariantsInNestedPlans() {
      this.shouldShowDistributeInitiativeModal = true
    },
    showTacticSummaryOverlay(info) {
      // Show planning item summary overlay with delay on first hover, no delay on subsequent hovers
      const planningItemSummaryDisplayDelay = this.isDisplayingSummaryPlanningItem ? 0 : 2000
      this.currentSummaryPlanningItemId = info.planningItem.id
      if (info.planningItem.originalTacticID) {
        this.currentSummaryPlanningItemId = info.planningItem.originalTacticID
        this.currentSummaryInstanceStartDate = info.planningItem.startDate
        this.currentSummaryInstanceEndDate = info.planningItem.endDate
      } else {
        this.currentSummaryInstanceStartDate = null
        this.currentSummaryInstanceEndDate = null
      }
      window.clearTimeout(this.currentSummaryTimeout)
      this.currentSummaryTimeout = window.setTimeout(() => {
        if (!this.$refs.planningItemSummaryOverlay) return
        // The hover target is a raw DOM node captured at mouseenter time. If it re-renders
        // and detaches before this delayed timeout fires, PrimeVue's OverlayPanel measures a
        // detached node as all zeros and renders pinned at the viewport's upper-left corner.
        if (!document.contains(info.event.target as Node)) return
        this.isDisplayingSummaryPlanningItem = true
        // @ts-expect-error
        this.$refs.planningItemSummaryOverlay.show({ ...info.event, currentTarget: info.event.target })
      }, planningItemSummaryDisplayDelay)
    },
    hideTacticSummaryOverlay() {
      // Hide planning item details overlay
      if (!this.$refs.planningItemSummaryOverlay) return
      // @ts-expect-error
      this.$refs.planningItemSummaryOverlay.hide()

      // Reset timeout after delay
      window.clearTimeout(this.currentSummaryTimeout)
      this.currentSummaryTimeout = window.setTimeout(() => {
        this.isDisplayingSummaryPlanningItem = false
        this.currentSummaryPlanningItemId = {} as ID
        this.currentSummaryInstanceStartDate = null
        this.currentSummaryInstanceEndDate = null
        window.clearTimeout(this.currentSummaryTimeout)
      }, 3000)
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.overstory-container {
  .p-accordion-header {
    &.p-disabled {
      display: none;
    }
  }

  .tactics-row {
    .tactic-button {
      min-height: 3rem;

      &.subscription {
        border: 0.1rem solid #999999;
      }
    }
  }

  .flow-accordion .p-accordion-header:after {
    content: none;
  }

  .flow-accordion .events-container {
    border-right: 0;
  }
}
</style>
